import React, { useState } from 'react'
import LocationForm from './LocationForm'
import axios from 'axios'
import { t } from '../../i18n'
import omit from 'lodash/omit'
import { getInitialSlots, SlotType } from './kitchenConfigurationHelper'
import KitchenConfigurationWarningModal from './KitchenConfigurationWarningModal'

const CreateLocationForm = (props) => {
  const [location, setLocation] = useState({
    name: '',
    currency_id: props.currencies[0].id,
    nonstop_working_hours: false,
    robot_connection_url: '',
    robot_connection_username: '',
    robot_connection_password: '',
    robot_certificate: '',
    robot_private_key: '',
    temperature_scale: 'celsius',
    weight_scale: 'metric',
    webhook_url: '',
    working_start_hour: null,
    working_end_hour: null,
    attachment: null,
    address: '',
    date_format: props.dateFormats[0],
    time_format: props.timeFormats[0],
    timezone: 'UTC',
    sensor_report_emails: [],
    critical_issue_phone_numbers: [],
    sensor_connection_url: '',
    ai_platform_enabled: false,
    ai_platform_workspace_id: ''
  })

  const [kitchenConfiguration, setKitchenConfiguration] = useState({
    fixed_container_slots: getInitialSlots(
      SlotType.FIXED_CONTAINER,
      props.kitchenConfigurationOptions
    ),
    mobile_container_slots: getInitialSlots(
      SlotType.MOBILE_CONTAINER,
      props.kitchenConfigurationOptions
    ),
    cooking_units: getInitialSlots(
      SlotType.COOKING_UNIT,
      props.kitchenConfigurationOptions
    ),
    plate_stands: getInitialSlots(
      SlotType.PLATE_STAND,
      props.kitchenConfigurationOptions
    ),
    locker_slots: getInitialSlots(
      SlotType.LOCKER,
      props.kitchenConfigurationOptions
    ),
    robot_trays: getInitialSlots(SlotType.ROBOT_TRAY, props.kitchenConfigurationOptions)
  })

  const [saveSuccessful, setSaveSuccessful] = useState(null)
  const [saveText, setSaveText] = useState('')
  const [configWarnings, setConfigWarnings] = useState([])
  const [kitchenConfigurationErrors, setKitchenConfigurationErrors] = useState({
    fixed_container_slots: false,
    mobile_container_slots: false,
    plate_stands: false,
    cooking_units: false,
    locker_slots: false
  })

  const providersWithoutProviderType = (orderProviders) => {
    return orderProviders.map((provider) => {
      const { provider_type, destroyable, ...rest } = provider
      return rest
    })
  }

  const saveLocation = (location, onFinished) => {
    const url = Routes.api_internal_locations_path({ format: 'json' });

    const formData = buildFormData(location);

    axios
      .post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((resp) => handleSaveSuccess(resp, onFinished))
      .catch((error) => handleSaveError(error, onFinished));
  };

  const buildFormData = (location) => {
    const formData = new FormData();

    const basicAttributes = [
      'name',
      'currency_id',
      'temperature_scale',
      'weight_scale',
      'webhook_url',
      'nonstop_working_hours',
      'working_start_hour',
      'working_end_hour',
      'robot_connection_url',
      'robot_connection_username',
      'robot_connection_password',
      'robot_certificate',
      'robot_private_key',
      'address',
      'date_format',
      'time_format',
      'timezone',
      'sensor_report_emails',
      'critical_issue_phone_numbers',
      'sensor_connection_url',
      'ai_platform_enabled',
      'ai_platform_workspace_id'
    ];

    basicAttributes.forEach((attr) => {
      if (location[attr] != null && location[attr] != undefined) {
        formData.append(attr, location[attr]);
      }
    })

    if (location.order_provider_locations_attributes) {
      formData.append(
        'order_provider_locations_attributes',
        JSON.stringify(providersWithoutProviderType(location.order_provider_locations_attributes))
      );
    }

    if (location.sensor_report_emails != null && location.sensor_report_emails != undefined) {
      formData.append('sensor_report_emails', JSON.stringify(location.sensor_report_emails));
    }

    if (location.critical_issue_phone_numbers != null && location.critical_issue_phone_numbers != undefined) {
      formData.append('critical_issue_phone_numbers', JSON.stringify(location.critical_issue_phone_numbers));
    }

    if (kitchenConfiguration) {
      formData.append('kitchen_configuration', JSON.stringify(kitchenConfiguration));
    }

    if (location.attachment) {
      formData.append('attachment', location.attachment);
    }

    return formData;
  };

  const handleSaveSuccess = (resp, onFinished) => {
    setSaveSuccessful(true);
    setSaveText(t('save_successful', { scope: 'locations' }));
    onFinished(true, null);
    setLocation({ ...resp.data.location, attachment: null });

    if (resp.data.warnings) {
      setConfigWarnings(resp.data.warnings);
    } else {
      window.location = '/admin/locations';
    }
  };

  const handleSaveError = (error, onFinished) => {
    setSaveSuccessful(false);
    setSaveText(t('save_failed', { scope: 'locations' }));
    onFinished(false, error.response?.data);
  };

  const renderSaveMessage = () =>
    saveSuccessful === null ? null : (
      <div className={`alert alert-${saveSuccessful ? 'success' : 'danger'}`}>
        {saveText}
      </div>
    )

  if (!location) {
    return <></>
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        {renderSaveMessage()}
        <LocationForm
          location={location}
          kitchenConfiguration={kitchenConfiguration}
          setKitchenConfiguration={setKitchenConfiguration}
          kitchenConfigurationOptions={props.kitchenConfigurationOptions}
          kitchenConfigurationErrors={kitchenConfigurationErrors}
          setKitchenConfigurationErrors={setKitchenConfigurationErrors}
          orderProviderOptions={props.orderProviderOptions}
          currencies={props.currencies}
          dateFormats={props.dateFormats}
          timeFormats={props.timeFormats}
          timezones={props.timezones}
          timezoneMappings={props.timezoneMappings}
          onSubmit={saveLocation}
          action="create"
        />
      </div>
      { configWarnings.length > 0 &&
        <KitchenConfigurationWarningModal
          onClose={() => setConfigWarnings([])}
          warnings={configWarnings}
          redirectToLocations={true}
        />
      }
    </div>
  )
}

export default CreateLocationForm
